<template>
    <div class="lp-product-picker">
        <!-- Same remote-search picker as the home editor (StorefrontHomeContent bestsellers). -->
        <el-select
            :value="multiple ? null : value || null"
            class="d-block"
            filterable
            remote
            reserve-keyword
            clearable
            :disabled="disabled || (multiple && ids.length >= max)"
            :placeholder="$t('marketing.lp.f.search_product')"
            :remote-method="search"
            :loading="searching"
            data-testid="lp-product-select"
            @change="onPick"
            @visible-change="onDropdown"
        >
            <el-option v-for="p in options" :key="p.id" :label="label(p)" :value="p.id">
                <span style="display: flex; align-items: center;">
                    <img v-if="p.image_url" :src="p.image_url" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px; margin-right: 8px;" alt="" />
                    <span>{{ label(p) }}</span>
                </span>
            </el-option>
        </el-select>

        <ul v-if="ids.length" class="list-unstyled mb-0 mt-2">
            <li v-for="(id, i) in ids" :key="id" class="d-flex align-items-center py-1 border-bottom lp-pick-row" :data-product-id="id">
                <img v-if="known[id] && known[id].image_url" :src="known[id].image_url" class="mr-2" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px;" alt="" />
                <span class="flex-fill small">{{ known[id] ? label(known[id]) : "#" + id }}</span>
                <el-tag v-if="unavailable.indexOf(id) !== -1" type="danger" size="mini" effect="dark" class="mr-2" data-testid="lp-unavailable">{{ $t("marketing.lp.err.unavailable_product") }}</el-tag>
                <template v-if="!disabled">
                    <base-button v-if="multiple" size="sm" type="neutral" class="p-1" :disabled="i === 0" :title="$t('marketing.lp.move_up')" @click="move(i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                    <base-button v-if="multiple" size="sm" type="neutral" class="p-1" :disabled="i === ids.length - 1" :title="$t('marketing.lp.move_down')" @click="move(i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                    <base-button size="sm" type="danger" class="p-1" :title="$t('marketing.lp.remove_section')" @click="remove(i)"><i class="fa fa-times"></i></base-button>
                </template>
            </li>
        </ul>
    </div>
</template>

<script>
import { Select, Option, Tag } from "element-ui";
import { LandingPageService } from "@/resource";

const normalize = (p) => ({ id: p.id, code: p.code || "", name: p.name || p.name_th || p.name_en || "", image_url: p.image_url || "" });

export default {
    name: "LpProductPicker",
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [Tag.name]: Tag
    },
    props: {
        // multiple → number[] (grid, ≤ max); single → number (featured; 0 = none)
        value: { type: [Array, Number], default: () => [] },
        multiple: { type: Boolean, default: false },
        max: { type: Number, default: 24 },
        unavailable: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false }
    },
    data() {
        return { searching: false, results: [], known: {} };
    },
    computed: {
        ids() {
            if (this.multiple) return Array.isArray(this.value) ? this.value : [];
            return Number(this.value) > 0 ? [Number(this.value)] : [];
        },
        options() {
            if (this.multiple) return this.results.filter((p) => this.ids.indexOf(p.id) === -1);
            // Single (featured): el-select shows the label of the matching
            // option, so the picked product must be an option even before a
            // search ran (after a reload it is only in `known`).
            const picked = this.ids.map((id) => this.known[id]).filter(Boolean);
            return picked.concat(this.results.filter((p) => this.ids.indexOf(p.id) === -1));
        }
    },
    watch: {
        ids: { immediate: true, handler: "seed" }
    },
    methods: {
        label(p) {
            return `${p.code || p.id} - ${p.name || ""}`;
        },
        async seed(ids) {
            const missing = (ids || []).filter((id) => !this.known[id]);
            await Promise.all(missing.map((id) => LandingPageService.getProduct(id).then(
                (r) => {
                    const p = (r.data && r.data.data) || r.data;
                    if (p && p.id) this.$set(this.known, p.id, normalize(p));
                },
                () => null
            )));
        },
        async search(query) {
            this.searching = true;
            try {
                const params = { page: 1, limit: 30, app_enable: 1 };
                if (query) params.keyword = query;
                const res = await LandingPageService.searchProducts(params);
                this.results = ((res.data && res.data.data) || []).map(normalize);
            } catch (e) {
                this.results = [];
            } finally {
                this.searching = false;
            }
        },
        onDropdown(open) {
            if (open && !this.results.length && !this.searching) this.search("");
        },
        onPick(id) {
            if (!id) {
                if (!this.multiple) this.$emit("input", 0);
                return;
            }
            const p = this.results.find((x) => x.id === id);
            if (p) this.$set(this.known, id, p);
            if (this.multiple) {
                if (this.ids.indexOf(id) === -1 && this.ids.length < this.max) this.$emit("input", this.ids.concat(id));
            } else {
                this.$emit("input", id);
            }
        },
        move(i, dir) {
            const arr = this.ids.slice();
            const j = i + dir;
            if (j < 0 || j >= arr.length) return;
            [arr[i], arr[j]] = [arr[j], arr[i]];
            this.$emit("input", arr);
        },
        remove(i) {
            if (!this.multiple) {
                this.$emit("input", 0);
                return;
            }
            const arr = this.ids.slice();
            arr.splice(i, 1);
            this.$emit("input", arr);
        }
    }
};
</script>
